import { useState } from "react";
import { useLocation } from "react-router-dom";
import AdminHeader from "../../header/admin_header";
import AdminSidebar from "../../sidebar/admin_sidebar";
import { useGhgReferences } from "../EmissionFactorBreakdown/useGhgReferences";
import FactorsTab from "./FactorsTab";
import SourcesTab from "./SourcesTab";
import { getEnergyFactors, getEnergyKpis, getEnergySources } from "./energyApi";
import { useEnergyList } from "./useEnergyList";
import "../KPIGrid/KPIGrid.css";
import "../GhgDatabases/GhgDatabases.css";
import "./EnergyFactors.css";

const TABS = Object.freeze([
  { key: "factors", label: "Energy factors" },
  { key: "sources", label: "Energy sources" },
]);

/**
 * The energy factors companies' energy is priced from and the sources they
 * belong to, on two tabs. Each tab lists, filters, adds, edits and deletes
 * its rows, and every value can cite a reference from the shared library.
 */
export default function EnergyFactors() {
  const location = useLocation();
  const [tab, setTab] = useState("factors");
  const sources = useEnergyList(getEnergySources, "Couldn't load the energy sources.");
  const factors = useEnergyList(getEnergyFactors, "Couldn't load the energy factors.");
  const kpis = useEnergyList(getEnergyKpis, "Couldn't load the energy KPIs.");
  const { references, reload: reloadReferences } = useGhgReferences();

  const linked = factors.rows.filter((factor) => factor.density_reference && factor.calorific_reference).length;

  /** Moves between the tabs with the arrow keys, as a tab list should. */
  const onTabKey = (e) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    const index = TABS.findIndex(({ key }) => key === tab);
    const next = TABS[(index + (e.key === "ArrowRight" ? 1 : TABS.length - 1)) % TABS.length];
    setTab(next.key);
    document.getElementById(`enf-tab-${next.key}`)?.focus();
  };

  return (
    <div>
      <AdminSidebar dataFromParent={location.pathname} />
      <AdminHeader />
      <div className="main_wrapper">
        <div className="inner_wraapper">
          <div className="kgb-root gdb-root">
            <div className="gdb-titlebar">
              <div>
                <h1 className="kgb-title">Energy Factors</h1>
                <p className="gdb-lead">How companies' fuel and electricity readings become energy (GJ), and where each value comes from.</p>
              </div>
            </div>

            <div className="gdb-stats">
              <div className="gdb-stat">
                <span className="gdb-stat-value">{factors.rows.length}</span>
                <span className="gdb-stat-label">Energy factors</span>
              </div>
              <div className="gdb-stat">
                <span className="gdb-stat-value">
                  {linked}
                  <small> / {factors.rows.length}</small>
                </span>
                <span className="gdb-stat-label">With both values linked</span>
              </div>
              <div className="gdb-stat">
                <span className="gdb-stat-value">{sources.rows.length}</span>
                <span className="gdb-stat-label">Energy sources</span>
              </div>
              <div className="gdb-stat">
                <span className="gdb-stat-value">{kpis.rows.length}</span>
                <span className="gdb-stat-label">Energy KPIs</span>
              </div>
            </div>

            {kpis.error ? (
              <div className="gdb-message gdb-message--error">
                {kpis.error}{" "}
                <button type="button" className="kgb-btn kgb-btn--quiet" onClick={kpis.reload}>
                  Retry
                </button>
              </div>
            ) : null}

            <div className="enf-tabs" role="tablist" aria-label="Energy factors and sources">
              {TABS.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  id={`enf-tab-${key}`}
                  aria-selected={tab === key}
                  aria-controls={`enf-panel-${key}`}
                  tabIndex={tab === key ? 0 : -1}
                  className={`enf-tab${tab === key ? " enf-tab--on" : ""}`}
                  onClick={() => setTab(key)}
                  onKeyDown={onTabKey}
                >
                  {label}
                  <span className="enf-tab-count">{key === "factors" ? factors.rows.length : sources.rows.length}</span>
                </button>
              ))}
            </div>

            {tab === "factors" ? (
              <FactorsTab
                factors={factors}
                sources={sources.rows}
                kpis={kpis.rows}
                references={references}
                onReferencesChanged={reloadReferences}
                onFactorsChanged={sources.reload}
              />
            ) : (
              <SourcesTab
                sources={sources}
                references={references}
                onReferencesChanged={reloadReferences}
                onSourcesChanged={factors.reload}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
